import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { buildCartMessage } from "@/lib/whatsapp";
import { type PaymentMethodId } from "@/lib/paymentMethods";
import { useSelectedPaymentMethod } from "@/hooks/useSelectedPaymentMethod";
import { trackWhatsAppClick } from "@/graphql/analytics";
import { client } from "@/lib/apolloClient";
import { useCart } from "@/contexts/CartContext";
import CartSummary from "./CartSummary";
import PaymentMethodSelector from "./PaymentMethodSelector";
import WhatsAppButton from "./WhatsAppButton";

export interface CartDrawerProps {
  phone: string | null;
  storeName?: string;
  cartMessageTemplate?: string | null;
  acceptedPaymentMethods: PaymentMethodId[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export default function CartDrawer({
  phone,
  storeName,
  cartMessageTemplate,
  acceptedPaymentMethods,
  open,
  onOpenChange,
}: CartDrawerProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [paymentMethodId, setPaymentMethodId] =
    useSelectedPaymentMethod(acceptedPaymentMethods);
  const { items, totalCents, storeId } = useCart();

  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;
  const setOpen = isControlled ? onOpenChange! : setInternalOpen;

  const cartMessage = buildCartMessage({
    items: items.map((i) => ({
      name: i.name,
      priceCents: i.priceCents,
      quantity: i.quantity,
    })),
    totalCents,
    storeName,
    template: cartMessageTemplate,
    paymentMethodId,
  });

  return (
    <Sheet open={isOpen} onOpenChange={setOpen}>
      <SheetContent className="flex flex-col font-light">
        <SheetHeader>
          <SheetTitle className="font-light tracking-tight">Carrinho</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-4 space-y-4">
          <CartSummary />
          {items.length > 0 && (
            <PaymentMethodSelector
              value={paymentMethodId}
              acceptedPaymentMethods={acceptedPaymentMethods}
              onChange={setPaymentMethodId}
            />
          )}
        </div>
        <SheetFooter>
          <WhatsAppButton
            phone={phone}
            message={cartMessage}
            className="w-full"
            onClick={() => {
              if (!storeId) return;
              trackWhatsAppClick(client, {
                storeId,
                eventType: "whatsapp_cart_clicked",
                metadata: {
                  itemCount: items.reduce((sum, i) => sum + i.quantity, 0),
                  productIds: items.map((i) => i.productId),
                  paymentMethodId,
                },
              });
            }}
          >
            Falar no WhatsApp
          </WhatsAppButton>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
